import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HEXAGON_PATH } from '@/lib/brand';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  /** Error text only; empty states otherwise rely on the title. */
  detail?: string;
  action?: React.ReactNode;
  className?: string;
}

/**
 * Brand-flavored empty state. Uses the hexagon container per Brand
 * Guidelines p.38 — this is one of the few places we earn the marketing-style
 * graphic device in the product UI.
 */
export function EmptyState({ icon: Icon, title, detail, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border bg-card/50 px-6 py-16 text-center',
        className,
      )}
    >
      <div className="relative flex h-16 w-16 items-center justify-center">
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 h-full w-full text-info"
        >
          <path
            d={HEXAGON_PATH}
            className="fill-info-soft/30"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinejoin="round"
          />
        </svg>
        {Icon && <Icon className="relative h-7 w-7 text-info" strokeWidth={1.5} />}
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-medium tracking-headline text-foreground">{title}</h3>
        {detail && <p className="max-w-sm text-sm text-muted-foreground">{detail}</p>}
      </div>
      {action}
    </div>
  );
}
